import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "@fmc-digital/i18n";
import { WhileYouWaitChecklist } from "../../molecules/WhileYouWaitChecklist";
import { cn, ErrorBanner, ErrorMessage, MFACodeInput, Button, type MFACodeInputRef } from "@fmc-digital/ui";
import { MFATimer } from "../../molecules/MFATimer";
import { getOtpExpirationMinutes } from "./mfa-code-container.constants";
import type { MFACodeContainerProps } from "./mfa-code-container.types";
import { useCountdownTimer } from "../../../hooks/useCountdownTimer";
import { useResendTimer } from "../../../hooks/useResendTimer";
import { ResendSection } from "../../../components/molecules/ResendSection";
import { useIsMinWidth768 } from "@/components/organisms/DownloadApp/useIsMinWidth768";
import {
  registerOtpBodyStyles,
  registerOtpContentWrapperStyles,
  registerOtpDigitMobileStyles,
  registerOtpInputMobileStyles,
  registerOtpInputStyles,
  registerOtpStackStyles,
} from "../../../pages/register/register-flow.styles";

type MFACodeContainerV2Props = Readonly<{
  factorState: MFACodeContainerProps["factorState"];
  onVerify?: MFACodeContainerProps["onVerify"];
  onExpire?: () => void;
  onValidate?: (code: string) => Promise<any>;
  onResend?: () => Promise<void> | void;
  onCancel?: () => void;
  inputError?: string | null;
  onInputEdit?: () => void;
  enableResendOnLoad?: boolean;
  onConsumeEnableResendOnLoad?: () => void;
}>;

export function MFACodeContainerV2(props: MFACodeContainerV2Props) {
  const { t } = useTranslation();
  const {
    factorState,
    onVerify,
    onExpire,
    onValidate,
    onResend,
    onCancel,
    inputError: externalInputError,
    onInputEdit,
    enableResendOnLoad,
    onConsumeEnableResendOnLoad,
  } = props;
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const codeInputRef = useRef<MFACodeInputRef>(null);
  const lastValidationAttemptRef = useRef<string | null>(null);
  const validationRequestIdRef = useRef(0);
  const verificationCompleteRef = useRef(false);
  const otpExpirationMinutes = getOtpExpirationMinutes(factorState.factorType);
  const { minutes, seconds, resetTimer } = useCountdownTimer(otpExpirationMinutes, onExpire);
  const { resendWait, setResendWait } = useResendTimer(30);
  const [isResending, setIsResending] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const isResendDisabled = isResending || resendWait > 0;
  const displayError = externalInputError ?? inputError;
  const isEmailFactor = factorState.factorType === "email";
  const isMdUp = useIsMinWidth768();

  useEffect(() => {
    if (
      minutes === 0 &&
      seconds === 0 &&
      !isSuccess &&
      !isValidating &&
      !verificationCompleteRef.current
    ) {
      setIsExpired(true);
    }
  }, [minutes, seconds, isSuccess, isValidating]);

  useEffect(() => {
    if (!enableResendOnLoad) return;
    setResendWait(0);
    onConsumeEnableResendOnLoad?.();
  }, [enableResendOnLoad, onConsumeEnableResendOnLoad, setResendWait]);

  const handleResend = useCallback(async () => {
    setIsResending(true);

    try {
      await onResend?.();

      setCode(["", "", "", "", "", ""]);
      setInputError(null);
      setIsSuccess(false);
      setIsExpired(false);
      verificationCompleteRef.current = false;
      lastValidationAttemptRef.current = null;
      if (onInputEdit) onInputEdit();

      resetTimer(getOtpExpirationMinutes(factorState.factorType));
      setResendWait(30);
    } catch {
      // Parent handler sets user-facing error state.
    } finally {
      setIsResending(false);
    }
  }, [resetTimer, setResendWait, onInputEdit, onResend, factorState.factorType]);

  const isCodeComplete = code.length === 6 && code.every((digit) => digit !== "");
  const completeCode = isCodeComplete ? code.join("") : null;

  useEffect(() => {
    if (!onValidate || !completeCode) return;
    if (lastValidationAttemptRef.current === completeCode) return;

    let didCancel = false;
    const codeToValidate = completeCode;
    const requestId = ++validationRequestIdRef.current;
    lastValidationAttemptRef.current = codeToValidate;

    const validate = async () => {
      setIsValidating(true);
      try {
        const response = await onValidate(codeToValidate);
        // eslint-disable-next-line no-console
        console.log("[MFACodeContainerV2] onValidate response:", response);
        if (didCancel || requestId !== validationRequestIdRef.current) return;
        const isValidationSuccess = response?.status === "SUCCESS" || response?.success === true;
        if (isValidationSuccess) {
          setIsSuccess(true);
          setInputError(null);
          verificationCompleteRef.current = true;
          setIsExpired(false);
          // eslint-disable-next-line no-console
          console.log("[MFACodeContainerV2] Calling onVerify after OTP success (no delay)", { code: codeToValidate, factorState });
          onVerify?.(codeToValidate, factorState);
        } else if (response?.status === "BANNER_ONLY") {
          setInputError(null);
        } else if (response?.status === "CLEAR_CODE") {
          setInputError(null);
          setIsSuccess(false);
          setCode(["", "", "", "", "", ""]);
          lastValidationAttemptRef.current = null;
        } else if (response?.status === "ALREADY_VALIDATED") {
          setInputError(null);
          verificationCompleteRef.current = true;
          setIsExpired(false);
          onVerify?.(codeToValidate, factorState);
        } else {
          setInputError(externalInputError ?? "The code you entered is invalid");
        }
      } catch {
        if (didCancel || requestId !== validationRequestIdRef.current) return;
        setInputError(externalInputError ?? "The code you entered is invalid");
      } finally {
        if (!didCancel && requestId === validationRequestIdRef.current) {
          setIsValidating(false);
        }
      }
    };
    validate();
    return () => {
      didCancel = true;
    };
  }, [completeCode, onVerify, onValidate, externalInputError, factorState]);

  return (
    <div className={cn(registerOtpBodyStyles())}>
      <div className={cn("mfa-code-content-wrapper", registerOtpContentWrapperStyles())}>
        <div className={cn("mfa-otp-stack", registerOtpStackStyles())}>
          <MFACodeInput
            ref={codeInputRef}
            code={code}
            onChange={(val) => {
              setInputError(null);
              setIsSuccess(false);
              verificationCompleteRef.current = false;
              setCode(val);
              if (onInputEdit) onInputEdit();
            }}
            error={!!displayError}
            disabled={isSuccess || isExpired}
            success={isSuccess}
            className={cn(
              registerOtpInputStyles(),
              !isMdUp && registerOtpInputMobileStyles(),
            )}
            digitClassName={!isMdUp ? cn(registerOtpDigitMobileStyles()) : undefined}
          />
          {displayError && (
            <div className="mfa-invalid-code-error-wrap">
              <ErrorMessage
                className="mfa-invalid-code-error"
                data-testid="mfa-invalid-code-error"
              >
                {displayError}
              </ErrorMessage>
            </div>
          )}
        </div>
        {!isExpired && (
          <div className="mt-4">
            <MFATimer minutes={minutes} seconds={seconds} />
          </div>
        )}
        <div className="mt-4 text-left">
          <ResendSection resendWait={resendWait} isResendDisabled={isResendDisabled} onResend={handleResend} />
        </div>
      </div>
      {isExpired && (
        <ErrorBanner
          message={t("auth.error.codeExpired")}
          className="mt-4 [&_span]:text-[14px] [&_span]:leading-5"
          testID="mfa-code-expired-banner"
        />
      )}
      {isEmailFactor && (
        <div className="mt-4">
          <WhileYouWaitChecklist />
        </div>
      )}
      {onCancel && (
        <div className="mt-4 flex flex-col-reverse lg:flex-row gap-4 lg:gap-8 mfa-code-buttons-wrapper">
          <Button
            variant="outlined"
            onClick={onCancel}
            className="w-full lg:flex-1"
            disabled={isExpired}
            data-testid="mfa-cancel-button"
          >
            {t("common.cancel")}
          </Button>
        </div>
      )}
    </div>
  );
}
